import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import {
  type ColumnSizingInfoState,
  flexRender,
  type Table,
} from '@tanstack/react-table';
import classNames from 'classnames';

import styles from './table-chart.module.css';

export const useGetHeaderRows = <TData extends object = object>({
  table,
  columnSizingInfo,
  onHeaderClick,
  autoFitColumns,
  resolveTextAlign,
}: {
  table: Table<TData>;
  columnSizingInfo: ColumnSizingInfoState;
  onHeaderClick?: (columnId: string) => void;
  autoFitColumns?: boolean;
  resolveTextAlign: (
    columnId: string
  ) => 'left' | 'center' | 'right' | undefined;
}): JSX.Element[] => {
  return table.getHeaderGroups().map((headerGroup) => (
    <tr key={headerGroup.id}>
      {headerGroup.headers.map((header, hIdx) => (
        <th
          key={header.id}
          onClick={(() => {
            if (columnSizingInfo.isResizingColumn) {
              return undefined;
            }
            if (onHeaderClick) {
              return () => onHeaderClick(header.column.id);
            }
            return header.column.getToggleSortingHandler();
          })()}
          aria-sort={((): 'ascending' | 'descending' | 'none' => {
            const sorted = header.column.getIsSorted();
            if (sorted === 'asc') return 'ascending';
            if (sorted === 'desc') return 'descending';
            return 'none';
          })()}
          className={classNames(
            styles.headerCell,
            header.column.getCanSort()
              ? styles.headerCellClickable
              : styles.headerCellNotClickable,
            hIdx === 0 && styles.firstCol
          )}
          style={{
            ...(autoFitColumns
              ? {}
              : {
                  width: `${header.column.getSize()}px`,
                  maxWidth: `${header.column.getSize()}px`,
                }),
            ...(resolveTextAlign(header.column.id)
              ? { textAlign: resolveTextAlign(header.column.id) }
              : {}),
          }}
        >
          <span
            className={styles.headerContent}
            style={(() => {
              const align = resolveTextAlign(header.column.id);
              if (align === undefined) return undefined;
              const justifyContentByAlign = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end',
              } as const;
              return {
                justifyContent: justifyContentByAlign[align],
                width: '100%',
              };
            })()}
          >
            <span className={styles.headerText}>
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
            </span>
            {header.column.getCanSort() && header.column.getIsSorted() && (
              <span className={styles.sortIcon}>
                {header.column.getIsSorted() === 'asc' ? (
                  <ArrowUpIcon boxSize={3} />
                ) : (
                  <ArrowDownIcon boxSize={3} />
                )}
              </span>
            )}
          </span>
          {header.column.getCanResize() && (
            <div
              onMouseDown={header.getResizeHandler()}
              onTouchStart={header.getResizeHandler()}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
              }}
              className={styles.resizer}
            />
          )}
        </th>
      ))}
    </tr>
  ));
};
